import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, CheckCircle2, Download, Mail, Receipt, RotateCcw, Save } from "lucide-react";

import api, { getStoredUser } from "../api";

function getCurrentUser() {
  return getStoredUser();
}

const PAYMENT_MODE_FIELDS = {
  CASH: [],
  UPI: [
    { key: "upi_app", label: "UPI App", placeholder: "PhonePe / GPay / Paytm" },
    { key: "upi_ref", label: "UPI Reference No", placeholder: "Enter UPI ref no" },
  ],
  NEFT: [
    { key: "bank_name", label: "Bank Name", placeholder: "Enter bank name" },
    { key: "account_holder", label: "Account Holder", placeholder: "Enter account holder name" },
    { key: "bank_ref", label: "NEFT Reference No", placeholder: "Enter NEFT ref no" },
  ],
  RTGS: [
    { key: "bank_name", label: "Bank Name", placeholder: "Enter bank name" },
    { key: "account_holder", label: "Account Holder", placeholder: "Enter account holder name" },
    { key: "bank_ref", label: "RTGS Reference No", placeholder: "Enter RTGS ref no" },
  ],
  CHEQUE: [
    { key: "bank_name", label: "Bank Name", placeholder: "Enter bank name" },
    { key: "account_holder", label: "Account Holder", placeholder: "Enter account holder name" },
    { key: "cheque_no", label: "Cheque No", placeholder: "Enter cheque number" },
    { key: "cheque_book_no", label: "Cheque Book No", placeholder: "Enter cheque book number" },
  ],
};

const createPaymentForm = () => ({
  payment_date: new Date().toISOString().slice(0, 10),
  amount: "",
  payment_mode: "CASH",
  reference_no: "",
  bank_name: "",
  account_holder: "",
  upi_app: "",
  upi_ref: "",
  bank_ref: "",
  cheque_no: "",
  cheque_book_no: "",
});

function buildPaymentReference(paymentForm) {
  const mode = String(paymentForm.payment_mode || "CASH").toUpperCase();
  const fields = PAYMENT_MODE_FIELDS[mode] || [];
  const parts = fields
    .map(({ key, label }) => {
      const value = String(paymentForm[key] || "").trim();
      return value ? `${label}: ${value}` : null;
    })
    .filter(Boolean);

  const fallback = String(paymentForm.reference_no || "").trim();
  if (fallback) parts.unshift(`Reference: ${fallback}`);
  return parts.join(" | ");
}

function InvoiceDetail({ invoiceId, onBack, onStatusChange }) {
  const [invoice, setInvoice] = useState(null);
  const [payments, setPayments] = useState([]);
  const [approvalLog, setApprovalLog] = useState([]);
  const [paymentForm, setPaymentForm] = useState(createPaymentForm());
  const [email, setEmail] = useState("");
  const [reviewComments, setReviewComments] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const currentUser = useMemo(() => getCurrentUser(), []);
  const isAdmin = Boolean(
    currentUser?.is_admin ||
      currentUser?.role === "org_admin" ||
      (currentUser?.access_matrix || []).some((access) => access.role === "org_admin")
  );
  const canSendInvoice = Boolean(String(email || "").trim());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [invoiceRes, paymentsRes, logRes] = await Promise.all([
        api.get(`/accounts/invoices/${invoiceId}`),
        api.get(`/accounts/invoices/${invoiceId}/payments`),
        api.get(`/accounts/invoices/${invoiceId}/approval-log`),
      ]);
      setInvoice(invoiceRes.data);
      setPayments(paymentsRes.data.data || []);
      setApprovalLog(logRes.data.data || []);
      setEmail(invoiceRes.data.customer?.contact_email || invoiceRes.data.sent_to_email || "");
      setReviewComments("");
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load invoice.");
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (invoiceId) load();
  }, [invoiceId, load]);

  const openPdf = () => {
    window.open(`http://127.0.0.1:5011/api/v1/accounts/invoices/${invoiceId}/pdf`, "_blank", "noopener,noreferrer");
  };

  const submitApproval = async () => {
    setActionLoading("submit");
    try {
      await api.post(`/accounts/invoices/${invoiceId}/submit-approval`);
      await load();
      onStatusChange?.();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit invoice for approval.");
    } finally {
      setActionLoading("");
    }
  };

  const recordPayment = async () => {
    setActionLoading("payment");
    try {
      const payload = {
        payment_date: paymentForm.payment_date,
        amount: paymentForm.amount,
        payment_mode: paymentForm.payment_mode,
        reference_no: buildPaymentReference(paymentForm),
      };
      await api.post(`/accounts/invoices/${invoiceId}/payment`, payload);
      setPaymentForm(createPaymentForm());
      await load();
      onStatusChange?.();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to record payment.");
    } finally {
      setActionLoading("");
    }
  };

  const reviewInvoice = async (action) => {
    setActionLoading(action);
    try {
      await api.post(`/accounts/invoices/${invoiceId}/approve`, {
        action,
        comments: reviewComments,
      });
      await load();
      onStatusChange?.();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} invoice.`);
    } finally {
      setActionLoading("");
    }
  };

  const sendInvoice = async () => {
    setActionLoading("send");
    try {
      await api.post(`/accounts/invoices/${invoiceId}/send`, { to_email: email });
      await load();
      onStatusChange?.();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send invoice.");
    } finally {
      setActionLoading("");
    }
  };

  if (loading) {
    return <div className="rounded-3xl border border-slate-200 bg-white p-8">Loading invoice details...</div>;
  }

  if (!invoice) {
    return <div className="rounded-3xl border border-rose-200 bg-rose-50 p-8 text-rose-700">Invoice not found.</div>;
  }

  const paymentModeFields = PAYMENT_MODE_FIELDS[paymentForm.payment_mode] || [];

  return (
    <div className="space-y-6">
      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="table-btn"><ArrowLeft size={15} /></button>
            <div>
              <h3 className="text-xl font-bold text-slate-900">{invoice.invoice_no}</h3>
              <p className="text-sm text-slate-500">{invoice.invoice_type} | {invoice.status} | {invoice.supply_type}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {invoice.status === "DRAFT" && (
              <button onClick={submitApproval} disabled={actionLoading === "submit"} className="primary-btn">
                <Save size={15} /> Submit for Approval
              </button>
            )}
            {isAdmin && invoice.status === "PENDING_APPROVAL" && (
              <>
                <button onClick={() => reviewInvoice("approve")} disabled={actionLoading === "approve"} className="approve-btn">
                  <CheckCircle2 size={15} /> Approve
                </button>
                <button onClick={() => reviewInvoice("reject")} disabled={actionLoading === "reject"} className="reject-btn">
                  <RotateCcw size={15} /> Return with Comments
                </button>
              </>
            )}
            {invoice.status !== "CANCELLED" && (
              <button onClick={sendInvoice} disabled={!canSendInvoice || actionLoading === "send"} className="primary-btn">
                <Mail size={15} /> Send to Customer
              </button>
            )}
            <button onClick={openPdf} className="secondary-btn"><Download size={15} /> PDF</button>
          </div>
        </div>

        <div className="grid gap-4 p-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            <div className="grid gap-4 rounded-2xl bg-slate-50 p-4 md:grid-cols-2">
              <Info label="Customer" value={invoice.customer?.name || invoice.customer_name || "-"} />
              <Info label="GSTIN" value={invoice.customer?.gstin || invoice.customer_gstin || "Non-GST"} />
              <Info label="Created By" value={invoice.created_by_name || "-"} />
              <Info label="Approved By" value={invoice.approved_by_name || "-"} />
              <Info label="Invoice Date" value={invoice.invoice_date} />
              <Info label="Due Date" value={invoice.due_date || "-"} />
              <Info label="Place of Supply" value={invoice.place_of_supply || "-"} />
              <Info label="Outstanding Balance" value={`Rs. ${Number(invoice.balance_due || 0).toFixed(2)}`} />
              <Info label="Address" value={invoice.customer?.full_address || invoice.customer_address || "-"} className="md:col-span-2" />
            </div>

            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">HSN</th>
                    <th className="px-4 py-3">Qty</th>
                    <th className="px-4 py-3">Rate</th>
                    <th className="px-4 py-3">Taxable</th>
                    <th className="px-4 py-3">Tax</th>
                    <th className="px-4 py-3">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoice.items || []).map((item) => (
                    <tr key={item.id} className="border-t border-slate-100">
                      <td className="px-4 py-3">{item.item_description}</td>
                      <td className="px-4 py-3">{item.item_type}</td>
                      <td className="px-4 py-3">{item.hsn_code || "-"}</td>
                      <td className="px-4 py-3">{Number(item.quantity || 0).toFixed(2)}</td>
                      <td className="px-4 py-3">Rs. {Number(item.rate || 0).toFixed(2)}</td>
                      <td className="px-4 py-3">Rs. {Number(item.taxable_amount || 0).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        {invoice.supply_type === "INTER"
                          ? `IGST ${Number(item.igst_amount || 0).toFixed(2)}`
                          : `CGST ${Number(item.cgst_amount || 0).toFixed(2)} / SGST ${Number(item.sgst_amount || 0).toFixed(2)}`}
                      </td>
                      <td className="px-4 py-3 font-semibold">Rs. {Number(item.total_amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {isAdmin && invoice.status === "PENDING_APPROVAL" && (
              <Panel title="Admin Review">
                <textarea
                  value={reviewComments}
                  onChange={(e) => setReviewComments(e.target.value)}
                  className="input min-h-[110px]"
                  placeholder="Add approval or rejection comments"
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => reviewInvoice("approve")} disabled={actionLoading === "approve"} className="approve-btn">
                    <CheckCircle2 size={15} /> Approve Invoice
                  </button>
                  <button onClick={() => reviewInvoice("reject")} disabled={actionLoading === "reject"} className="reject-btn">
                    <RotateCcw size={15} /> Return to Draft
                  </button>
                </div>
              </Panel>
            )}
          </div>

          <div className="space-y-4">
            <Panel title="Totals">
              <Line label="Subtotal" value={invoice.subtotal} />
              <Line label="Discount" value={invoice.discount_amount} />
              <Line label="Taxable Amount" value={invoice.taxable_amount} />
              <Line label="CGST" value={invoice.cgst_amount} />
              <Line label="SGST" value={invoice.sgst_amount} />
              <Line label="IGST" value={invoice.igst_amount} />
              <Line label="Making Charges" value={invoice.making_charges} />
              <Line label="Stone Charges" value={invoice.stone_charges} />
              <Line label="Other Charges" value={invoice.other_charges} />
              <Line label="Total" value={invoice.total_amount} strong />
              <Line label="Paid" value={invoice.amount_paid} />
              <Line label="Balance Due" value={invoice.balance_due} strong danger />
            </Panel>

            <Panel title="Send Invoice">
              <input value={email} onChange={(e) => setEmail(e.target.value)} className="input" placeholder="customer@email.com" />
              <button onClick={sendInvoice} disabled={!canSendInvoice || actionLoading === "send"} className="primary-btn mt-3"><Mail size={15} /> Send via SMTP</button>
              {!canSendInvoice && (
                <div className="mt-2 text-xs text-amber-700">
                  Enter a recipient email to enable sending.
                </div>
              )}
              {canSendInvoice && (
                <div className="mt-2 text-xs text-slate-500">
                  The customer email is auto-filled when available. You can also enter any email manually and send the invoice with due amount and attached PDF.
                </div>
              )}
            </Panel>

            <Panel title="Record Payment">
              <div className="grid gap-3">
                <input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm((prev) => ({ ...prev, payment_date: e.target.value }))} className="input" />
                <input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm((prev) => ({ ...prev, amount: e.target.value }))} className="input" placeholder="Amount" />
                <select value={paymentForm.payment_mode} onChange={(e) => setPaymentForm((prev) => ({ ...prev, payment_mode: e.target.value, reference_no: "" }))} className="input">
                  {["CASH", "UPI", "NEFT", "RTGS", "CHEQUE"].map((mode) => <option key={mode} value={mode}>{mode}</option>)}
                </select>
                {paymentModeFields.map((field) => (
                  <div key={field.key}>
                    <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{field.label}</div>
                    <input
                      value={paymentForm[field.key] || ""}
                      onChange={(e) => setPaymentForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      className="input"
                      placeholder={field.placeholder}
                    />
                  </div>
                ))}
                {paymentForm.payment_mode === "CASH" && (
                  <div className="rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-500">
                    Cash payment selected. No extra banking reference is required.
                  </div>
                )}
                {paymentForm.payment_mode !== "CASH" && (
                  <input
                    value={paymentForm.reference_no}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, reference_no: e.target.value }))}
                    className="input"
                    placeholder="Additional notes / fallback reference"
                  />
                )}
                <button onClick={recordPayment} disabled={actionLoading === "payment"} className="secondary-btn"><Receipt size={15} /> Record Payment</button>
              </div>
            </Panel>
          </div>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <Panel title="Payment History">
          {(payments || []).length ? payments.map((payment) => (
            <div key={payment.id} className="mb-3 rounded-xl bg-slate-50 p-3">
              <div className="font-semibold text-slate-900">Rs. {Number(payment.amount || 0).toFixed(2)}</div>
              <div className="text-sm text-slate-500">{payment.payment_date} | {payment.payment_mode}</div>
              <div className="mt-1 text-sm text-slate-600">{payment.reference_no || "-"}</div>
            </div>
          )) : <div className="text-sm text-slate-500">No payments recorded.</div>}
        </Panel>
        <Panel title="Approval Timeline">
          {(approvalLog || []).length ? approvalLog.map((log) => (
            <div key={log.id} className="mb-3 rounded-xl bg-slate-50 p-3">
              <div className="font-semibold text-slate-900">{log.action}</div>
              <div className="text-sm text-slate-500">{log.action_at} {log.action_by_name ? `• ${log.action_by_name}` : ""}</div>
              <div className="text-sm text-slate-600">{log.comments || "-"}</div>
            </div>
          )) : <div className="text-sm text-slate-500">No approval log available yet.</div>}
        </Panel>
      </section>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.65rem 0.85rem;
          background: white;
        }
        .primary-btn, .secondary-btn, .table-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
        }
        .primary-btn { background: rgb(79 70 229); color: white; }
        .secondary-btn, .table-btn { border: 1px solid rgb(203 213 225); background: white; color: rgb(51 65 85); }
        .approve-btn, .reject-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: white;
        }
        .approve-btn { background: rgb(22 163 74); }
        .reject-btn { background: rgb(220 38 38); }
      `}</style>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</div>
      {children}
    </div>
  );
}

function Info({ label, value, className = "" }) {
  return (
    <div className={className}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm text-slate-800">{value}</div>
    </div>
  );
}

function Line({ label, value, strong, danger }) {
  return (
    <div className={`mb-2 flex items-center justify-between text-sm ${strong ? "font-semibold" : ""} ${danger ? "text-rose-700" : "text-slate-700"}`}>
      <span>{label}</span>
      <span>Rs. {Number(value || 0).toFixed(2)}</span>
    </div>
  );
}

export default InvoiceDetail;
